import { toCanonicalKg, type DropsetRound, parseLocaleNumber } from '@heraion/shared';

import type { DraftSet } from './state/loggerDraftTypes';
import { draftDropsetRounds, type RawDropsetRound } from './dropsetDraft';

// THE raw-round → DropsetRound mapper for the logger's two rollup consumers:
// the header/footer totals (totalsMath.ts, canonical kg) and the timeline row
// chip (timelineRows.ts, display unit). Both feed the SAME shared
// dropsetSetTotals, so a divergence in how they read a round is a latent
// wrong-number bug of the worst kind ([[feedback_separation_of_concerns]] —
// two copies of a calculation that CAN drift).
//
// They used to keep twin copies of the parse + validity + assistance-sign
// rules, each carrying a TODO(cleanup) asking for this extraction the moment
// either one's number handling changed. IB-9 (bodyweight tonnage missing from
// the feed) is that moment: the row mapper had to learn the bodyweight branch
// the totals mapper already had.
//
// The ONLY legitimate difference between the two is the LOAD TRANSFORM: the
// totals canonicalise to kg, while the row deliberately keeps the magnitude
// the athlete typed so the chip sanity-checks against their own input
// (Antenore 2026-06-19). So the transform is the parameter; everything else is
// shared and can no longer drift.

export type LoadTransform = (magnitude: number) => number;

/** Totals path: the entered magnitude becomes canonical kg. */
export const canonicalLoad =
  (unit: 'kg' | 'lb'): LoadTransform =>
  (magnitude) =>
    toCanonicalKg(magnitude, unit);

/** Row-chip path: the entered magnitude IS the value (display unit). */
export const displayLoad: LoadTransform = (magnitude) => magnitude;

// Steps inherit the parent's display unit + laterality (the chain has no
// per-step unit/sides input).
export const toDropsetRound = (
  raw: RawDropsetRound,
  isBodyweight: boolean,
  loadedSides: 1 | 2,
  toLoad: LoadTransform,
  isTop = true,
): DropsetRound => {
  const repsParsed = parseLocaleNumber(raw.reps);
  const reps = Number.isFinite(repsParsed) ? repsParsed : 0;
  const w = parseLocaleNumber(raw.weight);
  // Sign-robust (D1, 2026-05-29). The field holds the magnitude; the sign
  // comes from EITHER a typed minus (`-10`) OR the extraNegative flag
  // (plate-calc "Assist"). The old `w > 0` guard silently discarded a typed
  // negative — that was the live bug (volume read bw-only, footer read 0).
  // Validity keys off non-zero, and the load is the magnitude.
  // See [[feedback_believe_bug_reports_over_code_reading]].
  const valid = Number.isFinite(w) && w !== 0;
  const load = valid ? toLoad(Math.abs(w)) : null;
  if (isBodyweight) {
    // Either input means assistance; both set → still negative once (no
    // double-negative cancellation).
    const isNegative = w < 0 || raw.extraNegative;
    // A blank chain step stays null so the myorep inherit rule can see it.
    const extraKg =
      load === null ? (isTop ? 0 : null) : load * (isNegative ? -1 : 1);
    return { kind: 'bodyweight', reps, extraKg, loadedSides };
  }
  return { kind: 'weighted', reps, weightKg: load, loadedSides };
};

// Ordered raw rounds for any set: a dropset uses the shared promote rule
// (top first); a plain set is a single round built from its own fields.
export const rawRoundsForSet = (s: DraftSet): RawDropsetRound[] =>
  s.dropset
    ? draftDropsetRounds(s)
    : [
        {
          reps: s.reps,
          weight: s.weight,
          extraNegative: s.extraNegative,
          completedAt: s.completedAt,
        },
      ];

// The "load moved per rep" of a BODYWEIGHT set: bw×pct/100 + signed extra,
// reps-agnostic. bw-INCLUSIVE by decision (D1 2026-05-29) so the number reads
// the REAL load instead of 0/`—` — a push-up is not a zero-load set, which is
// exactly what IB-9 reported ("push ups are not showing bodyweight weight").
// Collapses to the signed extra alone when the profile has no bodyweight.
//
// Reuses toDropsetRound for the top round so the assistance-sign rule (typed
// minus OR extraNegative, never double-negated) has ONE definition. `bodyweight`
// and the transform must agree on unit: canonical kg for the totals, display
// unit for the timeline row.
export const bodyweightPerRepLoad = (
  s: DraftSet,
  bodyweight: number,
  pct: number,
  toLoad: LoadTransform,
): number => {
  const top = rawRoundsForSet(s)[0];
  const round = top ? toDropsetRound(top, true, s.loadedSides, toLoad) : null;
  const extra = round?.kind === 'bodyweight' ? (round.extraKg ?? 0) : 0;
  return bodyweight * (pct / 100) + extra;
};
